import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, PoundSterling, Phone, MessageCircle } from "lucide-react";
import { COST_PAGES } from "@/lib/cost-pages";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/cost/")({
  head: () =>
    seoHead({
      path: "/cost",
      title: "Car Repair & MOT Cost Guides East London | Woodseer Autos",
      description:
        "Indicative pricing for MOT, servicing, brakes, clutch and diagnostics in East London. Clear quotes from Woodseer Autos, Poplar E14.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Cost guides", path: "/cost" },
      ],
    }),
  component: CostHub,
});

function CostHub() {
  const biz = getBusinessConfig();
  return (
    <article className="bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <p className="eyebrow text-primary">
            <PoundSterling className="inline h-3.5 w-3.5 mr-1" />
            Honest cost guides — no fixed online prices
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold text-foreground">
            What car repairs actually cost in East London
          </h1>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-3xl">
            We won't pretend to fix a price online — every car is different. These guides explain
            what affects the bill, what's included, and how to get a real quote in under a minute.
            All work is carried out at our Poplar E14 workshop, DVSA-authorised MOT station V106376.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={biz.phoneHref} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">
              <Phone className="h-4 w-4" /> {biz.phone}
            </a>
            <a href={biz.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1ea952]">
              <MessageCircle className="h-4 w-4" /> WhatsApp a quote
            </a>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COST_PAGES.map((p) => (
            <Link
              key={p.slug}
              to="/cost/$slug"
              params={{ slug: p.slug }}
              className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold text-foreground group-hover:text-primary">
                  {p.h1}
                </h2>
                <ArrowRight className="h-5 w-5 text-primary shrink-0 mt-1" />
              </div>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                {p.metaDescription}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
